import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    UserPlus, 
    Check, 
    X, 
    Clock, 
    Building2,
    Globe,
    Mail,
    Loader2,
    ExternalLink,
    Copy,
    Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

export default function AdminPosterRequestsPage() {
    const { token } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showApprovalResult, setShowApprovalResult] = useState(null);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            const response = await fetch(`${API}/admin/poster-requests?status=pending`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setRequests(data);
            }
        } catch (error) {
            toast.error('Error loading requests');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (requestId) => {
        setProcessing(requestId);
        try {
            const response = await fetch(`${API}/admin/poster-requests/${requestId}/approve`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const result = await response.json();
                setShowApprovalResult(result);
                setRequests(prev => prev.filter(r => r.id !== requestId));
                setSelectedRequest(null);
                toast.success('Poster approved successfully');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Approval failed');
            }
        } catch (error) {
            toast.error('Error approving request');
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = async (requestId) => {
        setProcessing(requestId);
        try {
            const response = await fetch(`${API}/admin/poster-requests/${requestId}/reject`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                setRequests(prev => prev.filter(r => r.id !== requestId));
                setSelectedRequest(null);
                toast.success('Request rejected');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Rejection failed');
            }
        } catch (error) {
            toast.error('Error rejecting request');
        } finally {
            setProcessing(null);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied to clipboard');
        } catch (err) {
            // Fallback for environments without clipboard access (like preview)
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                toast.success('Copied to clipboard');
            } catch (execErr) {
                toast.error('Failed to copy - please select and copy manually');
            }
            document.body.removeChild(textArea);
        }
    };

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-[#F51E1E]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-white">Poster Requests</h1>
                        <p className="text-[#A3A3A3] mt-1">Review and approve poster signup requests</p>
                    </div>
                    <Badge className="bg-[#F51E1E]/20 text-[#F51E1E] border-[#F51E1E]/30 px-3 py-1">
                        {requests.length} Pending
                    </Badge>
                </div>

                {/* Requests List */}
                {requests.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-[#1A1A1A] border border-[#333] rounded-xl p-12 text-center"
                    >
                        <UserPlus className="w-12 h-12 text-[#666] mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">No Pending Requests</h3>
                        <p className="text-[#A3A3A3]">All poster requests have been processed</p>
                    </motion.div>
                ) : (
                    <div className="grid gap-4">
                        <AnimatePresence>
                            {requests.map((request, idx) => (
                                <motion.div
                                    key={request.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -100 }}
                                    transition={{ delay: idx * 0.05 }}
                                    className="bg-[#1A1A1A] border border-[#333] rounded-xl p-6 hover:border-[#404040] transition-colors"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-12 h-12 rounded-full bg-[#262626] flex items-center justify-center">
                                                    <span className="text-lg font-bold text-white">
                                                        {request.first_name[0]}{request.last_name[0]}
                                                    </span>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold text-white">
                                                        {request.first_name} {request.last_name}
                                                    </h3>
                                                    <div className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                                                        <Mail className="w-3 h-3" />
                                                        <span>{request.email}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                                <div className="bg-[#262626] rounded-lg p-3">
                                                    <div className="flex items-center gap-2 text-[#888] text-xs mb-1">
                                                        <Building2 className="w-3 h-3" />
                                                        Company
                                                    </div>
                                                    <p className="text-white font-medium truncate">{request.company_name}</p>
                                                </div>
                                                <div className="bg-[#262626] rounded-lg p-3">
                                                    <div className="flex items-center gap-2 text-[#888] text-xs mb-1">
                                                        <Globe className="w-3 h-3" />
                                                        Website
                                                    </div>
                                                    {request.company_website ? (
                                                        <a 
                                                            href={request.company_website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-[#F51E1E] hover:underline text-sm flex items-center gap-1"
                                                        >
                                                            Visit <ExternalLink className="w-3 h-3" />
                                                        </a>
                                                    ) : (
                                                        <p className="text-[#666] text-sm">Not provided</p>
                                                    )}
                                                </div>
                                                <div className="bg-[#262626] rounded-lg p-3">
                                                    <div className="text-[#888] text-xs mb-1">Project Type</div>
                                                    <p className="text-white font-medium text-sm">{request.project_type}</p>
                                                </div>
                                                <div className="bg-[#262626] rounded-lg p-3">
                                                    <div className="flex items-center gap-2 text-[#888] text-xs mb-1">
                                                        <Clock className="w-3 h-3" />
                                                        Submitted
                                                    </div>
                                                    <p className="text-white text-sm">{formatDate(request.created_at)}</p>
                                                </div>
                                            </div>

                                            <div className="bg-[#262626] rounded-lg p-3">
                                                <div className="text-[#888] text-xs mb-2">Why they need sync music</div>
                                                <p className="text-[#A3A3A3] text-sm line-clamp-2">{request.project_description}</p>
                                                <button
                                                    onClick={() => setSelectedRequest(request)}
                                                    className="text-[#F51E1E] text-xs mt-2 hover:underline flex items-center gap-1"
                                                >
                                                    <Eye className="w-3 h-3" /> View full details
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex gap-2 ml-4">
                                            <Button
                                                onClick={() => handleReject(request.id)}
                                                disabled={processing === request.id}
                                                variant="outline"
                                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                onClick={() => handleApprove(request.id)}
                                                disabled={processing === request.id}
                                                className="bg-green-600 hover:bg-green-700 text-white"
                                            >
                                                {processing === request.id ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <Check className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
                <DialogContent className="bg-[#1A1A1A] border-[#333] text-white max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl">
                            {selectedRequest?.first_name} {selectedRequest?.last_name}
                        </DialogTitle>
                        <DialogDescription className="text-[#A3A3A3]">
                            {selectedRequest?.company_name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-[#888]">Email</Label>
                                <p className="text-white">{selectedRequest?.email}</p>
                            </div>
                            <div>
                                <Label className="text-[#888]">Project Type</Label>
                                <p className="text-white">{selectedRequest?.project_type}</p>
                            </div>
                        </div>
                        
                        {selectedRequest?.company_website && (
                            <div>
                                <Label className="text-[#888]">Website</Label>
                                <a 
                                    href={selectedRequest.company_website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#F51E1E] hover:underline flex items-center gap-1"
                                >
                                    {selectedRequest.company_website} <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        )}
                        
                        <div>
                            <Label className="text-[#888]">Why they need sync music</Label>
                            <p className="text-[#A3A3A3] mt-1 whitespace-pre-wrap">
                                {selectedRequest?.project_description}
                            </p>
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button
                            onClick={() => handleReject(selectedRequest?.id)}
                            disabled={processing === selectedRequest?.id}
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                            <X className="w-4 h-4 mr-2" /> Reject
                        </Button>
                        <Button
                            onClick={() => handleApprove(selectedRequest?.id)}
                            disabled={processing === selectedRequest?.id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                            {processing === selectedRequest?.id ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Check className="w-4 h-4 mr-2" />
                            )}
                            Approve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approval Result Modal */}
            <Dialog open={!!showApprovalResult} onOpenChange={() => setShowApprovalResult(null)}>
                <DialogContent className="bg-[#1A1A1A] border-[#333] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl text-green-400">Poster Approved!</DialogTitle>
                        <DialogDescription className="text-[#A3A3A3]">
                            Account created and GHL contact synced
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="bg-[#262626] rounded-lg p-4">
                            <Label className="text-[#888]">Email</Label>
                            <div className="flex items-center justify-between mt-1">
                                <p className="text-white">{showApprovalResult?.email}</p>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copyToClipboard(showApprovalResult?.email)}
                                    className="text-[#888] hover:text-white"
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        
                        <div className="bg-[#262626] rounded-lg p-4">
                            <Label className="text-[#888]">Initial Password</Label>
                            <div className="flex items-center justify-between mt-1">
                                <code className="text-[#F51E1E] font-mono">
                                    {showApprovalResult?.initial_password}
                                </code>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copyToClipboard(showApprovalResult?.initial_password)}
                                    className="text-[#888] hover:text-white"
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        
                        <p className="text-xs text-[#666]">
                            This password has been synced to the GHL contact's initial_password field.
                        </p>
                    </div>

                    <DialogFooter>
                        <Button
                            onClick={() => setShowApprovalResult(null)}
                            className="bg-[#F51E1E] hover:bg-[#D41212] text-white"
                        >
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function Label({ children, className = '' }) {
    return <label className={`text-sm font-medium ${className}`}>{children}</label>;
}
